import { useState, useEffect } from 'react';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea, ReferenceLine,
} from 'recharts';

// Matches the MOBILE_BREAKPOINT_PX/useIsMobile convention already used in
// AdminLandingPagePerformance.js on this same admin panel.
const MOBILE_BREAKPOINT_PX = 700;

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT_PX
    );
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT_PX);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return isMobile;
}

function SectionCard({ title, status, statusColor, collapsible, defaultOpen = true, children }) {
    const isMobile = useIsMobile();
    const [open, setOpen] = useState(defaultOpen);
    const showContent = !collapsible || open;
    return (
        <div style={{ marginBottom: '25px', padding: isMobile ? '12px' : '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: 'white' }}>
            <div
                style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: showContent ? '10px' : 0, cursor: collapsible ? 'pointer' : 'default', userSelect: 'none' }}
                onClick={collapsible ? () => setOpen((o) => !o) : undefined}
                role={collapsible ? 'button' : undefined}
                tabIndex={collapsible ? 0 : undefined}
                aria-expanded={collapsible ? open : undefined}
                onKeyDown={collapsible ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); }
                } : undefined}
            >
                {collapsible && (
                    <span style={{ fontSize: '11px', color: '#898781', display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                        ▶
                    </span>
                )}
                <h4 style={{ margin: 0 }}>{title}</h4>
                {status && (
                    <span style={{
                        padding: '2px 10px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        backgroundColor: statusColor?.bg || '#e2e3e5',
                        color: statusColor?.text || '#41464b'
                    }}>
                        {status}
                    </span>
                )}
            </div>
            {showContent && children}
        </div>
    );
}

const money = (n) => `£${n.toFixed(2)}`;

const makeVariation = (label, cpc, convertPct, subAmount) => ({
    label,
    cpc: String(cpc),
    convertPct: String(convertPct),
    subAmount: String(subAmount),
});

const DEFAULT_VARIATIONS = [
    makeVariation('Variation A', 1.50, 5, 20),
    makeVariation('Variation B', 2.00, 4, 20),
    makeVariation('Variation C', 1.00, 8, 15),
    makeVariation('Variation D', 3.00, 10, 30),
];

// Derives CAC and self-liquidating-offer economics from the three raw inputs
// plus an assumed monthly churn rate. CAC = cost per click / click-to-
// subscription conversion rate, since that's the average ad spend needed to
// land one subscriber.
function computeMetrics({ cpc, convertPct, subAmount }, churnPct) {
    const cpcNum = parseFloat(cpc);
    const convertNum = parseFloat(convertPct);
    const subAmountNum = parseFloat(subAmount);

    if (!(cpcNum > 0) || !(convertNum > 0) || !(subAmountNum >= 0)) {
        return null;
    }

    const conversionRate = convertNum / 100;
    const cac = cpcNum / conversionRate;
    const profitLoss = subAmountNum - cac;
    const isSelfLiquidating = profitLoss >= 0;
    // Highest CPC this combo could pay and still exactly recoup CAC on
    // the first subscription payment - useful as an "upper bound to bid" number.
    const maxViableCpc = subAmountNum * conversionRate;
    // Whole-month payback accounting for churn (see monthsToPaybackWithChurn) -
    // 1 means paid back on the very first/day-1 payment, null means it never
    // pays back within PAYBACK_CAP_MONTHS at this churn rate.
    const monthsToRecoup = monthsToPaybackWithChurn(cac, subAmountNum, churnPct);
    // The other two single-lever breakeven thresholds, holding everything
    // else fixed: cac is already "subscription price would need to be at
    // least this", and this is "conversion rate would need to be at least
    // this" (cac == subAmount when convertNum == cpcNum/subAmountNum*100).
    const requiredConvertPct = subAmountNum > 0 ? (cpcNum / subAmountNum) * 100 : Infinity;
    // Expected lifetime value under a constant monthly churn rate: a
    // subscriber's expected number of payments is 1/churn, so LTV = ARPU ÷
    // churn rate (the standard SaaS approximation, same convention as the
    // "subscription amount ÷ monthly churn rate" LTV note in the benchmarks
    // box above).
    const ltv = churnPct > 0 ? subAmountNum / (churnPct / 100) : Infinity;
    const ltvToCac = cac > 0 ? ltv / cac : Infinity;

    return {
        cac, profitLoss, isSelfLiquidating, maxViableCpc, monthsToRecoup, requiredConvertPct, ltv, ltvToCac,
        currentCpc: cpcNum, currentConvertPct: convertNum, currentSubAmount: subAmountNum,
    };
}

// Standard SaaS rule of thumb (see benchmarks note above): 3:1 is the usual
// minimum viable bar, 5:1+ is excellent.
function ltvCacColors(ratio) {
    if (!isFinite(ratio)) return { bg: '#f4f3ef', text: '#52514e' };
    if (ratio >= 5) return { bg: PROFIT_GOOD_BG, text: PROFIT_GOOD_TEXT };
    if (ratio >= 3) return { bg: PROFIT_WARN_BG, text: PROFIT_WARN_TEXT };
    return { bg: PROFIT_BAD_BG, text: PROFIT_BAD_TEXT };
}

function LtvCacSummary({ rows }) {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '25px' }}>
            {rows.map(({ v, metrics }, i) => {
                const ratio = metrics?.ltvToCac;
                const colors = ltvCacColors(ratio);
                return (
                    <div key={i} style={{
                        flex: '1 1 140px', border: '1px solid #ddd', borderRadius: '8px', padding: '12px 14px',
                        backgroundColor: colors.bg,
                    }}>
                        <div style={{ fontSize: '12px', color: colors.text, opacity: 0.85, marginBottom: '4px' }}>
                            {v.label} - LTV:CAC
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: 600, color: colors.text }}>
                            {metrics && isFinite(ratio) ? `${ratio.toFixed(1)} : 1` : '—'}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function VariationColumn({ variation, onChange, churnPct }) {
    const metrics = computeMetrics(variation, churnPct);

    const update = (field) => (e) => onChange({ ...variation, [field]: e.target.value });

    return (
        <div style={{
            flex: '1 1 220px',
            minWidth: '220px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '15px',
            backgroundColor: '#fafafa',
        }}>
            <input
                type="text"
                value={variation.label}
                onChange={update('label')}
                style={{
                    width: '100%',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    border: 'none',
                    background: 'transparent',
                    marginBottom: '12px',
                    padding: '4px 0',
                    borderBottom: '1px solid #ccc',
                }}
            />

            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '3px' }}>
                CPC (£)
            </label>
            <input
                type="number"
                step="0.01"
                min="0"
                value={variation.cpc}
                onChange={update('cpc')}
                style={{ width: '100%', padding: '6px 8px', marginBottom: '10px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
            />

            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '3px' }}>
                Click → Subscription conversion (%)
            </label>
            <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={variation.convertPct}
                onChange={update('convertPct')}
                style={{ width: '100%', padding: '6px 8px', marginBottom: '10px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
            />

            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '3px' }}>
                Subscription amount (£)
            </label>
            <input
                type="number"
                step="0.01"
                min="0"
                value={variation.subAmount}
                onChange={update('subAmount')}
                style={{ width: '100%', padding: '6px 8px', marginBottom: '15px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
            />

            <div style={{ borderTop: '1px solid #ddd', paddingTop: '10px' }}>
                {metrics ? (
                    <>
                        <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                            CAC: <strong>{gbpWhole(metrics.cac)}</strong>
                        </div>
                        <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                            Profit/Loss on 1st purchase:{' '}
                            <strong style={{ color: metrics.profitLoss >= 0 ? '#155724' : '#721c24' }}>
                                {metrics.profitLoss >= 0 ? '+' : ''}{gbpWhole(metrics.profitLoss)}
                            </strong>
                        </div>
                        {!metrics.isSelfLiquidating && (
                            <div style={{ fontSize: '13px', marginBottom: '8px', color: '#666' }}>
                                {metrics.monthsToRecoup != null
                                    ? `Payback by month ${metrics.monthsToRecoup} at ${churnPct}% monthly churn`
                                    : `Doesn't pay back within ${PAYBACK_CAP_MONTHS / 12} years at ${churnPct}% monthly churn`}
                            </div>
                        )}
                        <span style={{
                            display: 'inline-block',
                            marginTop: '4px',
                            marginBottom: '10px',
                            padding: '3px 10px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            backgroundColor: metrics.isSelfLiquidating ? '#d4edda' : '#f8d7da',
                            color: metrics.isSelfLiquidating ? '#155724' : '#721c24',
                        }}>
                            {metrics.isSelfLiquidating ? '✅ Self-liquidating' : '❌ Not self-liquidating'}
                        </span>

                        <div style={{ fontSize: '12px', color: '#666', borderTop: '1px dashed #ddd', paddingTop: '8px' }}>
                            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#444' }}>
                                {metrics.isSelfLiquidating ? 'Room before this stops breaking even:' : 'To break even, any one of:'}
                            </div>
                            <div style={{ marginBottom: '2px' }}>
                                CPC no higher than <strong>{money(metrics.maxViableCpc)}</strong> (currently {money(metrics.currentCpc)})
                            </div>
                            <div style={{ marginBottom: '2px' }}>
                                Subscription price at least <strong>{gbpWhole(metrics.cac)}</strong> (currently {gbpWhole(metrics.currentSubAmount)})
                            </div>
                            <div>
                                Conversion rate at least <strong>{metrics.requiredConvertPct.toFixed(1)}%</strong> (currently {metrics.currentConvertPct}%)
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{ fontSize: '13px', color: '#999' }}>Fill in CPC and conversion % to see results</div>
                )}
            </div>
        </div>
    );
}

// ── CAC breakeven explorer (fixed £ ranges) ────────────────────────────────
// Visualizes every combination of subscription amount x CPC x click->sub
// conversion rate where CAC == subscription amount (the same formula as the
// calculator above, just swept across a fixed grid instead of typed in by
// hand).

const SUB_AMOUNTS = [3, 4, 5, 6, 7, 8, 9]; // £, £1 steps
const CONVERT_RATES = [2, 4, 6, 8, 10]; // %, 2pt steps
const REALISTIC_CPC_MIN = 0.30; // £ - the CPC band actually seen on Meta
const REALISTIC_CPC_MAX = 0.90;

const gbp = (n) => `£${n.toFixed(2)}`;
// Whole-pound, comma-grouped form for tables where per-penny precision isn't
// useful and long numbers need to stay readable at a glance.
const gbpWhole = (n) => `£${Math.round(n).toLocaleString('en-GB')}`;
// Compact form for chart axes/tooltips that can reach large numbers (an
// ongoing reinvestment simulation compounds quickly) - full pennies aren't
// meaningful at that scale and a long "£120000.00" tick collides with the
// axis's own rotated label in the space available.
const gbpCompact = (n) => {
    const abs = Math.abs(n);
    if (abs >= 1000000) return `£${(n / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `£${(n / 1000).toFixed(1)}k`;
    return `£${n.toFixed(0)}`;
};

// CAC = CPC / conversion rate, so "CAC == subscription amount" solves to
// this - the CPC you'd need to hit for that (subAmount, convertPct) pair to
// exactly break even on the first payment.
const breakevenCpc = (subAmount, convertPct) => subAmount * (convertPct / 100);

const PAYBACK_CAP_MONTHS = 60; // 5 years - beyond this just call it "doesn't pay back"

// A subscriber pays subAmount every month they're still active, starting
// immediately at month 1, with the surviving fraction decaying by
// churnPct each month. Returns the first month whose cumulative collected
// revenue reaches cac (1 = paid back on the very first/day-1 payment), or
// null if it never does within PAYBACK_CAP_MONTHS - a subscriber's whole-
// lifetime revenue converges to subAmount / (churnPct/100) as months go on,
// so a high enough CAC relative to that (or a high enough churn rate)
// genuinely never pays back, not just "eventually, given more time".
function monthsToPaybackWithChurn(cac, subAmount, churnPct) {
    if (!(cac > 0)) return 0;
    if (!(subAmount > 0)) return null;
    const retention = 1 - churnPct / 100;
    let cumulative = 0;
    let active = 1;
    for (let month = 1; month <= PAYBACK_CAP_MONTHS; month += 1) {
        cumulative += active * subAmount;
        if (cumulative >= cac) return month;
        active *= retention;
    }
    return null;
}

// Fixed-order categorical palette (first 7 of the app's 8-hue sequence) -
// one hue per subscription-amount line/row, assigned in a stable order that
// never changes as lines are toggled.
const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];

// Single-hue sequential ramp (steps 100->700), used only for magnitude
// (the revenue-potential score) - never for series identity.
const BLUE_RAMP = ['#cde2fb', '#b7d3f6', '#9ec5f4', '#86b6ef', '#6da7ec', '#5598e7', '#3987e5', '#2a78d6', '#256abf', '#1c5cab', '#184f95', '#104281', '#0d366b'];

const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};
const rgbToHex = (r, g, b) => `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;

// Linear-RGB interpolation through the documented ramp's stops - keeps every
// rendered color a blend of two documented hexes rather than an invented one.
function sequentialColor(t) {
    const clamped = Math.max(0, Math.min(1, t));
    const pos = clamped * (BLUE_RAMP.length - 1);
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, BLUE_RAMP.length - 1);
    const frac = pos - i0;
    const [r0, g0, b0] = hexToRgb(BLUE_RAMP[i0]);
    const [r1, g1, b1] = hexToRgb(BLUE_RAMP[i1]);
    return rgbToHex(r0 + (r1 - r0) * frac, g0 + (g1 - g0) * frac, b0 + (b1 - b0) * frac);
}

// Picks white or ink text so a label sitting inside a colored fill always
// stays readable, by the fill's own luminance rather than a fixed choice.
function textOn(bgHex) {
    const [r, g, b] = hexToRgb(bgHex);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#0b0b0b' : '#ffffff';
}

// A rough, adjustable illustration of "volume of subscribers x subscription
// amount" (revenue) for each combination - NOT a forecast. Two assumptions
// are exposed as sliders because they're genuinely unknown, not measured:
//   - priceElasticity: how fast willingness-to-subscribe falls as price rises
//   - reachElasticity: how much extra Meta audience a higher affordable CPC
//     buys you (diminishing returns, since the addressable audience is finite)
// raw = subAmount x demand(subAmount) x reach(breakevenCpc), then min-max
// normalized to 0-10 across the whole grid so the sliders always produce a
// full-range, comparable score.
function computeRevenueScores(priceElasticity, reachElasticity) {
    const cells = SUB_AMOUNTS.flatMap((subAmount) => CONVERT_RATES.map((convertPct) => {
        const cpc = breakevenCpc(subAmount, convertPct);
        const demand = Math.pow(1 / subAmount, priceElasticity);
        const reach = Math.pow(cpc, reachElasticity);
        return { subAmount, convertPct, cpc, raw: subAmount * demand * reach };
    }));
    const values = cells.map((c) => c.raw);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    return cells.map((c) => ({ ...c, score: ((c.raw - min) / span) * 10 }));
}

// Native range inputs jump their value to wherever you click/tap on the
// track, not just the thumb - easy to trigger by accident while trying to
// scroll past a slider. This blocks that: only a pointer landing within
// THUMB_HIT_TOLERANCE_PX of the thumb's current position is allowed to
// start a drag: anything further along the track is treated as a miss (or
// a scroll attempt) and ignored, rather than snapping the value there.
const THUMB_HIT_TOLERANCE_PX = 22; // roughly a fingertip's radius, per standard touch-target guidance
function guardSliderThumb(e) {
    const el = e.currentTarget;
    const min = parseFloat(el.min);
    const max = parseFloat(el.max);
    const value = parseFloat(el.value);
    if (!(max > min)) return;
    const rect = el.getBoundingClientRect();
    const fraction = (value - min) / (max - min);
    const thumbX = rect.left + fraction * rect.width;
    if (Math.abs(e.clientX - thumbX) > THUMB_HIT_TOLERANCE_PX) {
        e.preventDefault();
    }
}

const btnStyle = (active) => ({
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: active ? 'bold' : 'normal',
    border: '1px solid ' + (active ? '#2a78d6' : '#ccc'),
    borderRadius: '6px',
    backgroundColor: active ? '#eaf2fc' : 'white',
    color: active ? '#2a78d6' : '#444',
    cursor: 'pointer',
});

function CpcAssumptionControl({ assumedCpc, setAssumedCpc }) {
    return (
        <label style={{ fontSize: '12px', color: '#52514e', display: 'block', marginBottom: '18px', maxWidth: '460px' }}>
            Assumed real CPC you'd actually pay ({gbp(assumedCpc)})
            <input
                type="range" min={REALISTIC_CPC_MIN} max={REALISTIC_CPC_MAX} step="0.01"
                value={assumedCpc}
                onChange={(e) => setAssumedCpc(parseFloat(e.target.value))}
                onPointerDown={guardSliderThumb}
                style={{ width: '100%', touchAction: 'pan-y' }}
            />
            <span style={{ display: 'block', color: '#898781' }}>
                This is the one thing that decides which combinations below are self-liquidating from day 1.
            </span>
        </label>
    );
}

function ChurnAssumptionControl({ churnRate, setChurnRate }) {
    return (
        <label style={{ fontSize: '12px', color: '#52514e', display: 'block', marginBottom: '18px', maxWidth: '460px' }}>
            Assumed monthly churn ({churnRate}% cancel each month)
            <input
                type="range" min="0" max="40" step="1"
                value={churnRate}
                onChange={(e) => setChurnRate(parseInt(e.target.value, 10))}
                onPointerDown={guardSliderThumb}
                style={{ width: '100%', touchAction: 'pan-y' }}
            />
            <span style={{ display: 'block', color: '#898781' }}>
                Drives the payback-period figures below - CAC alone doesn't say how long it actually takes to earn
                back if some subscribers cancel before you do.
            </span>
        </label>
    );
}

function ExplorerLegend({ hiddenSeries, onToggle }) {
    return (
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '8px' }}>
            {SUB_AMOUNTS.map((subAmount, i) => {
                const isHidden = hiddenSeries.has(subAmount);
                return (
                    <button
                        key={subAmount}
                        type="button"
                        onClick={() => onToggle(subAmount)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none',
                            cursor: 'pointer', padding: '2px 4px', fontSize: '13px',
                            color: isHidden ? '#9ca3af' : '#374151',
                            textDecoration: isHidden ? 'line-through' : 'none',
                        }}
                    >
                        <span style={{
                            display: 'inline-block', width: '16px', height: '2px',
                            background: isHidden ? '#d1d5db' : SERIES_COLORS[i],
                        }} />
                        £{subAmount}/mo
                    </button>
                );
            })}
        </div>
    );
}

function ExplorerTooltip({ active, payload, label }) {
    if (!active || !payload || payload.length === 0) return null;
    const sorted = [...payload].sort((a, b) => b.value - a.value);
    return (
        <div style={{
            background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
            padding: '10px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: '13px',
        }}>
            <div style={{ fontWeight: 600, marginBottom: '6px', color: '#111827' }}>Conversion rate: {label}</div>
            {sorted.map((entry) => (
                <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0' }}>
                    <span style={{ width: '14px', height: '2px', background: entry.color, flexShrink: 0 }} />
                    <span style={{ color: '#6b7280' }}>{entry.name}:</span>
                    <span style={{ fontWeight: 600, color: '#111827' }}>breakeven CPC {gbp(entry.value)}</span>
                </div>
            ))}
        </div>
    );
}

function BreakevenLineChart({ hiddenSeries, onToggle, assumedCpc }) {
    const data = CONVERT_RATES.map((rate) => {
        const point = { rate: `${rate}%` };
        SUB_AMOUNTS.forEach((subAmount) => {
            point[`sub${subAmount}`] = breakevenCpc(subAmount, rate);
        });
        return point;
    });

    return (
        <>
            <ResponsiveContainer width="100%" height={340}>
                <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#e1e0d9" vertical={false} />
                    <ReferenceArea
                        y1={REALISTIC_CPC_MIN}
                        y2={REALISTIC_CPC_MAX}
                        fill="#898781"
                        fillOpacity={0.08}
                        label={{ value: 'Typical CPC range (£0.30–£0.90)', position: 'insideTopLeft', fontSize: 11, fill: '#898781' }}
                    />
                    <XAxis dataKey="rate" tick={{ fontSize: 12, fill: '#52514e' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                    <YAxis
                        domain={[0, 1]}
                        tickFormatter={(v) => `£${v.toFixed(2)}`}
                        tick={{ fontSize: 12, fill: '#52514e' }}
                        axisLine={{ stroke: '#c3c2b7' }}
                        tickLine={false}
                        label={{ value: 'Breakeven CPC', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#52514e' }}
                    />
                    <ReferenceLine
                        y={assumedCpc}
                        stroke="#0b0b0b"
                        strokeWidth={2}
                        strokeDasharray="5 3"
                        label={{ value: `Your CPC: ${gbp(assumedCpc)}`, position: 'insideBottomRight', fontSize: 11, fill: '#0b0b0b' }}
                    />
                    <Tooltip content={<ExplorerTooltip />} cursor={{ stroke: '#c3c2b7' }} />
                    {SUB_AMOUNTS.map((subAmount, i) => (
                        <Line
                            key={subAmount}
                            type="monotone"
                            dataKey={`sub${subAmount}`}
                            name={`£${subAmount}/mo`}
                            stroke={SERIES_COLORS[i]}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                            hide={hiddenSeries.has(subAmount)}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
            <ExplorerLegend hiddenSeries={hiddenSeries} onToggle={onToggle} />
            <p style={{ fontSize: '12px', color: '#898781', marginTop: '10px', marginBottom: 0 }}>
                Each line is the CPC that makes CAC exactly equal that subscription price at each conversion rate.
                Wherever your assumed CPC (the dashed line) sits <em>below</em> a subscription-amount line, that
                combo is self-liquidating; where it's above, CAC exceeds the subscription amount and you lose
                money on day 1.
            </p>
        </>
    );
}

// Exactly the colors/convention the "Ads profitability calculator" above
// already uses for its self-liquidating badge - reused here so the two
// sections read as one system rather than inventing a second status palette.
const PROFIT_GOOD_BG = '#d4edda';
const PROFIT_GOOD_TEXT = '#155724';
const PROFIT_BAD_BG = '#f8d7da';
const PROFIT_BAD_TEXT = '#721c24';
const PROFIT_WARN_BG = '#fff3cd';
const PROFIT_WARN_TEXT = '#856404';

function ProfitabilityHeatmap({ assumedCpc, churnRate }) {
    const isMobile = useIsMobile();
    const cellPad = isMobile ? '4px 2px' : '8px 10px';
    const headPad = isMobile ? '3px 2px' : '6px 10px';
    const profitFontSize = isMobile ? '13px' : '18px';
    const cacFontSize = isMobile ? '9px' : '11px';

    return (
        <>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'separate', borderSpacing: isMobile ? '2px' : '3px', fontSize: isMobile ? '11px' : '13px', width: isMobile ? '100%' : 'auto', tableLayout: isMobile ? 'fixed' : 'auto' }}>
                    <thead>
                        <tr>
                            <th style={{ padding: headPad, textAlign: 'left', width: isMobile ? '15%' : 'auto' }}>{isMobile ? '' : 'Sub. amount \\ Conversion'}</th>
                            {CONVERT_RATES.map((rate) => (
                                <th key={rate} style={{ padding: headPad, fontWeight: 600, color: '#52514e' }}>{rate}%</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {SUB_AMOUNTS.map((subAmount) => (
                            <tr key={subAmount}>
                                <td style={{ padding: headPad, fontWeight: 600, color: '#52514e', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>£{subAmount}{isMobile ? '' : '/mo'}</td>
                                {CONVERT_RATES.map((rate) => {
                                    // Same math as the calculator above (CAC = CPC / conversion rate), just
                                    // evaluated at this cell's subscription amount/conversion rate using the
                                    // one assumed real CPC, rather than a per-cell breakeven CPC.
                                    const cac = assumedCpc / (rate / 100);
                                    const profitLoss = subAmount - cac;
                                    const isSelfLiquidating = profitLoss >= 0;
                                    const bg = isSelfLiquidating ? PROFIT_GOOD_BG : PROFIT_BAD_BG;
                                    const text = isSelfLiquidating ? PROFIT_GOOD_TEXT : PROFIT_BAD_TEXT;
                                    const paybackMonths = monthsToPaybackWithChurn(cac, subAmount, churnRate);
                                    const paybackLabel = paybackMonths != null ? `${paybackMonths}mo` : `${PAYBACK_CAP_MONTHS / 12}yr+`;
                                    return (
                                        <td
                                            key={rate}
                                            title={`£${subAmount}/mo at ${rate}% conversion, ${gbp(assumedCpc)} CPC → CAC ${gbpWhole(cac)}, day-1 profit/loss ${profitLoss >= 0 ? '+' : ''}${gbpWhole(profitLoss)} (breakeven CPC would be ${gbp(breakevenCpc(subAmount, rate))}). Payback at ${churnRate}% monthly churn: ${paybackMonths != null ? `month ${paybackMonths}` : `doesn't pay back within ${PAYBACK_CAP_MONTHS / 12} years`}.`}
                                            style={{
                                                padding: cellPad,
                                                textAlign: 'center',
                                                borderRadius: isMobile ? '4px' : '6px',
                                                backgroundColor: bg,
                                                color: text,
                                                wordBreak: isMobile ? 'break-word' : 'normal',
                                            }}
                                        >
                                            <div style={{ fontSize: profitFontSize, fontWeight: 700, whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
                                                {isMobile ? '' : (isSelfLiquidating ? '✅ ' : '❌ ')}{profitLoss >= 0 ? '+' : ''}{gbpWhole(profitLoss)}
                                            </div>
                                            <div style={{ fontSize: cacFontSize, opacity: 0.85, whiteSpace: isMobile ? 'normal' : 'nowrap' }}>{isMobile ? gbpWhole(cac) : `CAC ${gbpWhole(cac)}`}</div>
                                            <div style={{ fontSize: cacFontSize, opacity: 0.85, whiteSpace: isMobile ? 'normal' : 'nowrap' }}>{isMobile ? paybackLabel : `Payback ${paybackLabel}`}</div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '16px', flexWrap: 'wrap', fontSize: '11px', color: '#898781', marginTop: '12px' }}>
                <span>
                    <span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: PROFIT_GOOD_BG, border: '1px solid ' + PROFIT_GOOD_TEXT, marginRight: '4px' }} />
                    ✅ Self-liquidating (CAC ≤ subscription amount)
                </span>
                <span>
                    <span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: PROFIT_BAD_BG, border: '1px solid ' + PROFIT_BAD_TEXT, marginRight: '4px' }} />
                    ❌ Loses money on day 1
                </span>
            </div>
            <p style={{ fontSize: '12px', color: '#898781', marginTop: '10px', marginBottom: 0 }}>
                Each cell shows the CAC (at your assumed real CPC above) versus that subscription amount, the
                resulting day-1 profit/loss if that person subscribes, and the whole-month payback period at your
                assumed churn rate above (1 month = paid back on the very first payment; {PAYBACK_CAP_MONTHS / 12}yr+
                means it never fully pays back at that churn rate - a subscriber's whole lifetime revenue converges
                to subscription amount ÷ monthly churn rate, so a high enough CAC relative to that genuinely never
                recovers, not just "eventually, given more time"). CAC = CPC ÷ conversion rate, profit/loss =
                subscription amount − CAC. Drag the sliders to see which combinations flip.
            </p>
        </>
    );
}

function ScoreLegend() {
    const stops = [0, 2.5, 5, 7.5, 10];
    return (
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', fontSize: '11px', color: '#898781', marginTop: '12px' }}>
            <span>Lower revenue potential</span>
            <div style={{ display: 'flex' }}>
                {stops.map((s) => (
                    <span key={s} style={{ width: '22px', height: '10px', backgroundColor: sequentialColor(s / 10) }} />
                ))}
            </div>
            <span>Higher revenue potential</span>
            <span>┄ dashed border = breakeven CPC outside £0.30–£0.90</span>
        </div>
    );
}

function RevenueAssumptionsControls({ priceElasticity, setPriceElasticity, reachElasticity, setReachElasticity }) {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '25px', marginBottom: '15px' }}>
            <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 260px' }}>
                Price sensitivity of demand ({priceElasticity.toFixed(1)})
                <input
                    type="range" min="0.2" max="2.5" step="0.1"
                    value={priceElasticity}
                    onChange={(e) => setPriceElasticity(parseFloat(e.target.value))}
                    onPointerDown={guardSliderThumb}
                    style={{ width: '100%', touchAction: 'pan-y' }}
                />
                <span style={{ display: 'block', color: '#898781' }}>Higher = fewer people willing to pay as price rises</span>
            </label>
            <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 260px' }}>
                Reach gained from a higher CPC ({reachElasticity.toFixed(1)})
                <input
                    type="range" min="0.1" max="1.5" step="0.1"
                    value={reachElasticity}
                    onChange={(e) => setReachElasticity(parseFloat(e.target.value))}
                    onPointerDown={guardSliderThumb}
                    style={{ width: '100%', touchAction: 'pan-y' }}
                />
                <span style={{ display: 'block', color: '#898781' }}>Higher = affording a pricier CPC buys a lot more Meta audience</span>
            </label>
        </div>
    );
}

function RevenuePotentialHeatmap({ priceElasticity, setPriceElasticity, reachElasticity, setReachElasticity }) {
    const isMobile = useIsMobile();
    const cellPad = isMobile ? '4px 2px' : '8px 10px';
    const headPad = isMobile ? '3px 2px' : '6px 10px';
    const scored = computeRevenueScores(priceElasticity, reachElasticity);
    const scoreFor = (subAmount, convertPct) => scored.find((c) => c.subAmount === subAmount && c.convertPct === convertPct);

    return (
        <>
            <RevenueAssumptionsControls
                priceElasticity={priceElasticity}
                setPriceElasticity={setPriceElasticity}
                reachElasticity={reachElasticity}
                setReachElasticity={setReachElasticity}
            />
            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'separate', borderSpacing: isMobile ? '2px' : '3px', fontSize: isMobile ? '11px' : '13px', width: isMobile ? '100%' : 'auto', tableLayout: isMobile ? 'fixed' : 'auto' }}>
                    <thead>
                        <tr>
                            <th style={{ padding: headPad, textAlign: 'left', width: isMobile ? '15%' : 'auto' }}>{isMobile ? '' : 'Sub. amount \\ Conversion'}</th>
                            {CONVERT_RATES.map((rate) => (
                                <th key={rate} style={{ padding: headPad, fontWeight: 600, color: '#52514e' }}>{rate}%</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {SUB_AMOUNTS.map((subAmount) => (
                            <tr key={subAmount}>
                                <td style={{ padding: headPad, fontWeight: 600, color: '#52514e', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>£{subAmount}{isMobile ? '' : '/mo'}</td>
                                {CONVERT_RATES.map((rate) => {
                                    const cell = scoreFor(subAmount, rate);
                                    const bg = sequentialColor(cell.score / 10);
                                    const outOfRange = cell.cpc < REALISTIC_CPC_MIN || cell.cpc > REALISTIC_CPC_MAX;
                                    return (
                                        <td
                                            key={rate}
                                            title={`£${subAmount}/mo at ${rate}% conversion needs a ${gbp(cell.cpc)} CPC to break even. Illustrative revenue-potential score: ${cell.score.toFixed(1)}/10.`}
                                            style={{
                                                padding: cellPad,
                                                textAlign: 'center',
                                                borderRadius: isMobile ? '4px' : '6px',
                                                backgroundColor: bg,
                                                color: textOn(bg),
                                                border: outOfRange ? '2px dashed ' + textOn(bg) : '2px solid transparent',
                                                wordBreak: isMobile ? 'break-word' : 'normal',
                                            }}
                                        >
                                            <div style={{ fontWeight: 700, whiteSpace: isMobile ? 'normal' : 'nowrap' }}>{gbp(cell.cpc)}</div>
                                            <div style={{ fontSize: isMobile ? '9px' : '11px', opacity: 0.85 }}>{cell.score.toFixed(1)}/10</div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <ScoreLegend />
        </>
    );
}

function CacBreakevenExplorer() {
    const [hiddenSeries, setHiddenSeries] = useState(new Set());
    const [assumedCpc, setAssumedCpc] = useState(0.50);
    const [churnRate, setChurnRate] = useState(10);
    const [showRevenuePotential, setShowRevenuePotential] = useState(false);
    const [priceElasticity, setPriceElasticity] = useState(1.3);
    const [reachElasticity, setReachElasticity] = useState(0.6);

    const toggleSeries = (subAmount) => {
        setHiddenSeries((prev) => {
            const next = new Set(prev);
            if (next.has(subAmount)) next.delete(subAmount); else next.add(subAmount);
            return next;
        });
    };

    return (
        <SectionCard title="CAC breakeven explorer" collapsible defaultOpen={false}>
            <p style={{ fontSize: '13px', color: '#666', marginTop: 0, marginBottom: '15px' }}>
                The main question this answers: for a given subscription amount (£3–£9), CPC (£0.30–£0.90) and
                click→subscription conversion rate (2%–10%), is CAC at or below the subscription amount - i.e. is
                that combination <strong>self-liquidating</strong>, paying for itself on the very first payment?
                Formula-only - not tied to real ad spend or Stripe data.
            </p>

            <CpcAssumptionControl assumedCpc={assumedCpc} setAssumedCpc={setAssumedCpc} />
            <ChurnAssumptionControl churnRate={churnRate} setChurnRate={setChurnRate} />

            <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#52514e' }}>Profitability (day 1) & payback period</h4>
            <ProfitabilityHeatmap assumedCpc={assumedCpc} churnRate={churnRate} />

            <h4 style={{ margin: '20px 0 8px 0', fontSize: '13px', color: '#52514e' }}>Breakeven line chart</h4>
            <BreakevenLineChart hiddenSeries={hiddenSeries} onToggle={toggleSeries} assumedCpc={assumedCpc} />

            <div style={{ marginTop: '10px' }}>
                <button type="button" style={btnStyle(showRevenuePotential)} onClick={() => setShowRevenuePotential((v) => !v)}>
                    {showRevenuePotential ? 'Hide' : 'Show'} revenue potential (illustrative)
                </button>
            </div>
            {showRevenuePotential && (
                <div style={{ marginTop: '15px' }}>
                    <RevenuePotentialHeatmap
                        priceElasticity={priceElasticity}
                        setPriceElasticity={setPriceElasticity}
                        reachElasticity={reachElasticity}
                        setReachElasticity={setReachElasticity}
                    />
                </div>
            )}

            <div style={{ borderTop: '1px solid #eee', marginTop: '20px', paddingTop: '15px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>How to think about optimizing this</h4>
                <ul style={{ fontSize: '13px', color: '#666', margin: 0, paddingLeft: '18px' }}>
                    <li>Start with the <strong>profitability</strong> grid: set your assumed real CPC above and read
                        off which (price, conversion rate) combinations are self-liquidating today versus which lose
                        money on day 1 and only become worthwhile through later renewals.</li>
                    <li>A combo that loses money on day 1 isn't necessarily bad - the <strong>payback period</strong>
                        (also in the grid) says how many months of renewals it actually takes to earn CAC back at
                        your assumed churn rate. Two combos with the same day-1 loss can have very different
                        payback periods once churn is factored in - see the Budget & cohort model below for a full
                        month-by-month breakdown of one specific scenario.</li>
                    <li>Subscription price is the only one of the three you set directly. CPC and conversion rate
                        are outcomes of effort, not dials - better targeting/creative brings CPC down, a better
                        landing page/onboarding brings conversion up.</li>
                    <li>A lower price needs a cheaper CPC or a better conversion rate to hit the same breakeven CAC
                        - the breakeven line chart shows how much room a price change actually buys you.</li>
                    <li>The revenue-potential score is a deliberately adjustable illustration of a separate
                        tradeoff: a lower price likely converts more visitors but earns less per subscriber, while
                        a higher price affords a higher breakeven CPC and so can outbid more of Meta's auction for
                        reach - but only if enough people are actually willing to pay it. It is not a forecast;
                        the two sliders are your own assumptions, not measured data.</li>
                </ul>
            </div>
        </SectionCard>
    );
}

// ── Budget & cohort financial model ────────────────────────────────────────
const wholeNum = (n) => Math.round(n).toLocaleString('en-GB');

// Models an ONGOING campaign that keeps acquiring subscribers every month -
// each month,
// existing cohorts (one per month spend happened, each decaying by churn
// since its own acquisition month) generate revenue, which funds the next
// spend decision:
//   - Before the campaign has paid for itself overall (cumulative income
//     hasn't yet caught up with cumulative spend), spend up to
//     monthlyBudget - a realistic ceiling on how much could actually be
//     deployed in a month (creative/account/Meta pacing limits) while still
//     funding growth out of pocket, throttled down further if that would
//     push cumulative net outlay past maxOutlay.
//   - Once cumulative income has caught up with cumulative spend, switch to
//     self-funding: every month's profit is split exactly profitTakePct%
//     into an untouched savings pot and the rest reinvested into new spend
//     - uncapped by monthlyBudget from this point on, since a business
//     funding its own growth out of already-earned profit isn't limited by
//     the same realistic-pacing ceiling that applied while still spending
//     down an initial deficit; reinvestment simply scales with however much
//     profit the campaign is actually generating.
// A month's own new spend also creates a cohort that pays back some revenue
// that same month (the "day 1" payment) - included in that month's
// income/active-subscriber figures immediately,
// but not credited back into that same month's own spend-sizing decision
// (which only looks at revenue from already-existing cohorts) - so this
// month's own new cohort can't inflate this month's own spend budget.
// profitTakePct may be a fixed number, or a function(priorMonthWithdrawal,
// profitAvailableThisMonth) => pct - the latter lets a caller vary the split
// over time (e.g. a staged
// plan that takes more out while chasing a near-term target, then less once
// that's reached and the goalpost moves further out).
function simulateReinvestment({ subAmount, cpc, convertPct, churnPct, maxOutlay, monthlyBudget, profitTakePct, months }) {
    const retention = 1 - churnPct / 100;
    const cohorts = []; // { acquisitionMonth, subscribers }
    let cumSpend = 0;
    let cumIncome = 0;
    let cumWithdrawn = 0; // the untouched savings pot
    let peakOutlay = 0;
    let breakEvenMonth = null;
    const rows = [];
    const resolvePct = typeof profitTakePct === 'function' ? profitTakePct : () => profitTakePct;

    for (let month = 1; month <= months; month += 1) {
        let revenueFromExisting = 0;
        for (const cohort of cohorts) {
            revenueFromExisting += cohort.subscribers * Math.pow(retention, month - cohort.acquisitionMonth) * subAmount;
        }

        const netOutlayBeforeSpend = cumSpend - (cumIncome + revenueFromExisting);
        const overallNetPosition = cumIncome - cumSpend; // as of the end of last month

        let spend;
        let withdrawal;
        let activePct = null;
        if (cumSpend > 0 && overallNetPosition >= 0) {
            // Self-funding: this month's incoming revenue IS the profit to
            // allocate, split exactly activePct / (100-activePct) - no
            // monthly cap on the reinvested share.
            const profitAvailable = Math.max(0, revenueFromExisting);
            activePct = resolvePct(rows.length ? rows[rows.length - 1].withdrawal : 0, profitAvailable);
            withdrawal = profitAvailable * (activePct / 100);
            spend = profitAvailable - withdrawal;
        } else {
            // Still working off the initial investment - spend up to our
            // realistic monthly budget, throttled down if that would push
            // cumulative loss past the risk cap.
            const outlayHeadroom = Math.max(0, maxOutlay - netOutlayBeforeSpend);
            spend = Math.min(monthlyBudget, outlayHeadroom);
            withdrawal = 0;
        }

        const newSubscribers = cpc > 0 ? (spend / cpc) * (convertPct / 100) : 0;
        if (newSubscribers > 0) cohorts.push({ acquisitionMonth: month, subscribers: newSubscribers });
        const newCohortRevenue = newSubscribers * subAmount;
        const revenueThisMonth = revenueFromExisting + newCohortRevenue;

        cumSpend += spend;
        cumIncome += revenueThisMonth;
        cumWithdrawn += withdrawal;

        const netPosition = cumIncome - cumSpend;
        peakOutlay = Math.max(peakOutlay, -netPosition);
        if (breakEvenMonth === null && netPosition >= 0) breakEvenMonth = month;

        const activeSubscribers = cohorts.reduce(
            (sum, c) => sum + c.subscribers * Math.pow(retention, month - c.acquisitionMonth), 0
        );

        rows.push({
            month, spend, revenue: revenueThisMonth, withdrawal, activePct,
            cumSpend, cumIncome, cumWithdrawn, netPosition, activeSubscribers,
        });
    }

    return { rows, peakOutlay, breakEvenMonth, cumWithdrawn };
}

function StatTile({ label, value, valueColor }) {
    return (
        <div style={{ flex: '1 1 160px', border: '1px solid #ddd', borderRadius: '8px', padding: '12px 14px', backgroundColor: '#fafafa' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{label}</div>
            <div style={{ fontSize: '20px', fontWeight: 600, color: valueColor || '#111827' }}>{value}</div>
        </div>
    );
}

function BudgetModelInputs({ inputs, setInputs }) {
    const set = (field) => (e) => setInputs((prev) => ({ ...prev, [field]: parseFloat(e.target.value) }));

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
            <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                Subscription amount ({gbpWhole(inputs.subAmount)}/mo)
                <input type="range" min="3" max="9" step="1" value={inputs.subAmount} onChange={set('subAmount')} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
            </label>
            <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                CPC ({gbp(inputs.cpc)})
                <input type="range" min={REALISTIC_CPC_MIN} max={REALISTIC_CPC_MAX} step="0.01" value={inputs.cpc} onChange={set('cpc')} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
            </label>
            <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                Click→subscription conversion ({inputs.convertPct}%)
                <input type="range" min="2" max="10" step="1" value={inputs.convertPct} onChange={set('convertPct')} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
            </label>
            <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                Monthly churn ({inputs.churnPct}% cancel before their next month)
                <input type="range" min="0" max="50" step="1" value={inputs.churnPct} onChange={set('churnPct')} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
            </label>
            <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                Monthly ad budget ({gbpWhole(inputs.monthlyBudget)})
                <input type="range" min="25" max="1000" step="25" value={inputs.monthlyBudget} onChange={set('monthlyBudget')} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
                <span style={{ display: 'block', color: '#898781' }}>The realistic cap on what you could spend in any one month while still funding growth out of pocket - lifted once the campaign is self-funding</span>
            </label>
            <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                Max outlay at risk ({gbpWhole(inputs.maxOutlay)})
                <input type="range" min="25" max="1000" step="25" value={inputs.maxOutlay} onChange={set('maxOutlay')} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
                <span style={{ display: 'block', color: '#898781' }}>Throttles the monthly budget down if cumulative spend would get this far ahead of cumulative income</span>
            </label>
            <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                Profit taken out each month ({inputs.profitTakePct}%)
                <input type="range" min="0" max="100" step="5" value={inputs.profitTakePct} onChange={set('profitTakePct')} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
                <span style={{ display: 'block', color: '#898781' }}>Once overall income has caught up with spend - the rest gets reinvested, uncapped</span>
            </label>
            <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                Months to project ({inputs.months})
                <input type="range" min="3" max="36" step="1" value={inputs.months} onChange={set('months')} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
            </label>
            <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                Target monthly profit taken out ({gbpWhole(inputs.targetMonthlyWithdrawal)})
                <input type="range" min="1000" max="20000" step="500" value={inputs.targetMonthlyWithdrawal} onChange={set('targetMonthlyWithdrawal')} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
                <span style={{ display: 'block', color: '#898781' }}>Used below to find which % taken out reaches this fastest</span>
            </label>
        </div>
    );
}

function CohortTrendChart({ data, dataKey, name, color, yFormatter, yLabel }) {
    return (
        <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#e1e0d9" vertical={false} />
                <ReferenceLine y={0} stroke="#c3c2b7" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#52514e' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} label={{ value: 'Month', position: 'insideBottom', offset: -2, fontSize: 11, fill: '#898781' }} />
                <YAxis
                    tickFormatter={yFormatter}
                    tick={{ fontSize: 11, fill: '#52514e' }}
                    axisLine={{ stroke: '#c3c2b7' }}
                    tickLine={false}
                    width={70}
                    label={{ value: yLabel, angle: -90, position: 'insideLeft', fontSize: 11, fill: '#52514e' }}
                />
                <Tooltip
                    formatter={(v) => yFormatter(v)}
                    labelFormatter={(m) => `Month ${m}`}
                    contentStyle={{ fontSize: '12px', borderRadius: '8px' }}
                />
                <Line type="monotone" dataKey={dataKey} name={name} stroke={color} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            </LineChart>
        </ResponsiveContainer>
    );
}

// Once a scenario is self-funding, revenue/spend/profit all compound by a
// fixed monthly multiplier g = retention * (1 + (subAmount/CAC) * reinvestShare) -
// derived from the spend recurrence in simulateReinvestment above. No cap in
// this formula-only model - see churnCutoffPct for the churn rate at which
// this scenario's growth would flip from compounding to shrinking.
function computeGrowthRate({ subAmount, cpc, convertPct, churnPct, profitTakePct }) {
    const cac = convertPct > 0 ? cpc / (convertPct / 100) : Infinity;
    const reinvestShare = 1 - profitTakePct / 100;
    const multiplier = isFinite(cac) && cac > 0 ? (subAmount / cac) * reinvestShare : 0;
    const retention = 1 - churnPct / 100;
    const g = retention * (1 + multiplier);
    const churnCutoffPct = (1 - 1 / (1 + multiplier)) * 100;
    return { g, cac, churnCutoffPct };
}

// How many months until a given profitTakePct first produces a month whose
// profit-taken-out (withdrawal) reaches the target - searches well beyond the
// visible "months to project" window since a low take-out % can take a long
// time to compound its way to a large monthly figure.
const OPTIMIZER_MONTHS_CAP = 240;
function monthsToReachMonthlyWithdrawal(inputs, profitTakePct, target) {
    const result = simulateReinvestment({ ...inputs, profitTakePct, months: OPTIMIZER_MONTHS_CAP });
    const hit = result.rows.find((r) => r.withdrawal >= target);
    return hit ? hit.month : null;
}

// Taking a bigger % out sooner means less gets reinvested, which slows the
// compounding growth rate - so there's a genuine trade-off, not a monotonic
// "higher % = faster" relationship. Finds it by brute-force search over the
// same 5%-step granularity as the profitTakePct slider.
function findFastestProfitTakePct(inputs, target) {
    const candidates = [];
    for (let p = 5; p <= 95; p += 5) {
        candidates.push({ profitTakePct: p, month: monthsToReachMonthlyWithdrawal(inputs, p, target) });
    }
    const reachable = candidates.filter((c) => c.month !== null);
    const best = reachable.length
        ? reachable.reduce((a, b) => (b.month < a.month ? b : a))
        : null;
    return { candidates, best };
}

function ProfitTakeOptimizer({ inputs }) {
    const target = inputs.targetMonthlyWithdrawal;
    const { candidates, best } = findFastestProfitTakePct(inputs, target);
    const currentMonth = monthsToReachMonthlyWithdrawal(inputs, inputs.profitTakePct, target);

    return (
        <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#52514e' }}>
                Fastest split to reach {gbpWhole(target)}/month taken out
            </h4>
            <p style={{ fontSize: '12px', color: '#898781', marginTop: 0, marginBottom: '10px' }}>
                {best ? (
                    <>
                        Taking <strong>{best.profitTakePct}%</strong> out each month gets there fastest - by{' '}
                        <strong>month {best.month}</strong>
                        {currentMonth && currentMonth !== best.month ? ` (your current ${inputs.profitTakePct}% gets there by month ${currentMonth})` : ''}
                        {!currentMonth ? ` (your current ${inputs.profitTakePct}% never reaches it within ${OPTIMIZER_MONTHS_CAP} months)` : ''}.
                        Take too little and reinvestment compounds slowly toward a bigger eventual %; take too much
                        and there's less left to reinvest, so growth (and the pot each % is taken from) slows down -
                        the fastest split balances the two.
                    </>
                ) : (
                    `None of the 5-95% splits reach ${gbpWhole(target)}/month within ${OPTIMIZER_MONTHS_CAP} months for this scenario - try a lower target or better unit economics (CPC, conversion, price) above.`
                )}
            </p>
            <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                            <th style={{ padding: '8px' }}>% taken out</th>
                            <th style={{ padding: '8px' }}>Month {gbpWhole(target)}/mo is reached</th>
                        </tr>
                    </thead>
                    <tbody>
                        {candidates.map((c) => {
                            const isBest = best && c.profitTakePct === best.profitTakePct;
                            return (
                                <tr
                                    key={c.profitTakePct}
                                    style={{ borderBottom: '1px solid #eee', backgroundColor: isBest ? PROFIT_GOOD_BG : 'transparent' }}
                                >
                                    <td style={{ padding: '8px', fontWeight: isBest ? 600 : 400 }}>{c.profitTakePct}%</td>
                                    <td style={{ padding: '8px', fontWeight: isBest ? 600 : 400 }}>
                                        {c.month ? `Month ${c.month}` : `Not within ${OPTIMIZER_MONTHS_CAP} months`}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// A single fixed take-out % isn't actually what you want if you have a
// sequence of goals rather than one target - e.g. "£2k/month ASAP, then
// £4k, then £10k, then £20k". Reinvesting 100% always compounds fastest
// (see the growth-rate formula above - reinvestShare=1 maximises it), so
// the fastest way to any one goal is: reinvest everything until you can
// comfortably afford it, THEN withdraw exactly that goal's £ amount every
// month (not a %) so as little as possible is diverted from compounding
// toward the next one - moving up to the next goal's amount the moment
// profit makes THAT comfortably affordable too. "Comfortably affordable"
// means the goal is at most thresholdPct% of that month's profit, so at
// least (100-thresholdPct)% is still being reinvested the moment you start
// withdrawing (rising further as profit keeps growing beyond the trigger,
// since the withdrawal itself stays capped at the goal's fixed £ amount).
// minFloorPct is an unconditional safety net underneath this - always bank
// at least that % of profit even before the first goal becomes affordable,
// so a bad month later doesn't find nothing saved at all.
function simulateStagedReinvestment(inputs, milestones, thresholdPct, minFloorPct) {
    const resolvePct = (priorWithdrawal, profitAvailable) => {
        if (!(profitAvailable > 0)) return 0;
        let target = 0;
        for (const m of milestones) {
            if (profitAvailable * (thresholdPct / 100) >= m) target = m;
            else break; // milestones are sorted ascending, so later ones can't be affordable either
        }
        const floorAmount = profitAvailable * (minFloorPct / 100);
        const withdrawAmount = Math.max(target, floorAmount);
        return Math.min(100, (withdrawAmount / profitAvailable) * 100);
    };
    const result = simulateReinvestment({ ...inputs, profitTakePct: resolvePct, months: OPTIMIZER_MONTHS_CAP });
    return result;
}

function parseMilestones(text) {
    return text
        .split(',')
        .map((s) => parseFloat(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b);
}

function StagedProfitPlan({ inputs }) {
    const [milestoneText, setMilestoneText] = useState('2000, 4000, 10000, 20000');
    const [thresholdPct, setThresholdPct] = useState(25);
    const [minFloorPct, setMinFloorPct] = useState(5);
    const milestones = parseMilestones(milestoneText);
    const hasMilestones = milestones.length > 0;

    const staged = hasMilestones ? simulateStagedReinvestment(inputs, milestones, thresholdPct, minFloorPct) : null;
    const hitRows = hasMilestones
        ? milestones.map((target) => staged.rows.find((r) => r.withdrawal >= target) || null)
        : [];
    const monthReachedFixed = hasMilestones
        ? milestones.map((target) => monthsToReachMonthlyWithdrawal(inputs, inputs.profitTakePct, target))
        : [];

    return (
        <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#52514e' }}>Staged profit-taking plan</h4>
            <p style={{ fontSize: '12px', color: '#898781', marginTop: 0, marginBottom: '10px' }}>
                Reinvest everything (fastest possible compounding) until the current goal is comfortably affordable,
                then withdraw exactly that goal's £ amount each month - not a % - so as little as possible is
                diverted from growing toward the next one. "Comfortably affordable" means the goal is at most the
                threshold below, so at least the rest keeps compounding the moment withdrawals start (and more as
                profit keeps growing beyond that point, since the withdrawal stays fixed at the goal amount). A
                lower threshold delays the first withdrawal but reaches later goals faster overall; a higher one
                banks the near-term goal sooner at the cost of the later ones - there's no single right answer, it
                depends which goal matters most to hit quickly.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', color: '#52514e', flex: '2 1 300px' }}>
                    Profit goals in order (£, comma-separated)
                    <input
                        type="text"
                        value={milestoneText}
                        onChange={(e) => setMilestoneText(e.target.value)}
                        style={{ width: '100%', padding: '6px 8px', marginTop: '4px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
                    />
                </label>
                <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                    Withdraw once goal ≤ this % of profit ({thresholdPct}%)
                    <input
                        type="range" min="10" max="75" step="5" value={thresholdPct}
                        onChange={(e) => setThresholdPct(parseInt(e.target.value, 10))}
                        onPointerDown={guardSliderThumb}
                        style={{ width: '100%', touchAction: 'pan-y' }}
                    />
                    <span style={{ display: 'block', color: '#898781' }}>
                        Lower = wait longer, reinvest more when you start; higher = start withdrawing the goal
                        sooner, reinvest less at that point
                    </span>
                </label>
                <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                    Minimum % taken out regardless ({minFloorPct}%)
                    <input
                        type="range" min="0" max="30" step="1" value={minFloorPct}
                        onChange={(e) => setMinFloorPct(parseInt(e.target.value, 10))}
                        onPointerDown={guardSliderThumb}
                        style={{ width: '100%', touchAction: 'pan-y' }}
                    />
                    <span style={{ display: 'block', color: '#898781' }}>
                        A safety floor - always bank at least this much, even before the first goal is affordable, so
                        a bad month later doesn't find nothing saved at all
                    </span>
                </label>
            </div>
            {hasMilestones ? (
                <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                                <th style={{ padding: '8px' }}>Goal</th>
                                <th style={{ padding: '8px' }}>% of profit when reached</th>
                                <th style={{ padding: '8px' }}>Reached (staged plan)</th>
                                <th style={{ padding: '8px' }}>Reached (fixed {inputs.profitTakePct}%)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {milestones.map((target, i) => {
                                const hit = hitRows[i];
                                return (
                                    <tr key={target} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '8px' }}>{gbpWhole(target)}/mo</td>
                                        <td style={{ padding: '8px' }}>{hit ? `${hit.activePct.toFixed(0)}%` : '—'}</td>
                                        <td style={{ padding: '8px', fontWeight: 600 }}>
                                            {hit ? `Month ${hit.month}` : `Not within ${OPTIMIZER_MONTHS_CAP} months`}
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                            {monthReachedFixed[i] ? `Month ${monthReachedFixed[i]}` : `Not within ${OPTIMIZER_MONTHS_CAP} months`}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p style={{ fontSize: '12px', color: PROFIT_BAD_TEXT }}>Enter at least one positive goal amount.</p>
            )}
        </div>
    );
}

function ReinvestmentView({ inputs }) {
    const result = simulateReinvestment(inputs);
    const chartData = result.rows.map((r) => ({
        month: r.month,
        withdrawal: r.withdrawal,
        activeSubscribers: r.activeSubscribers,
    }));
    const lastRow = result.rows.length ? result.rows[result.rows.length - 1] : null;
    const finalActive = lastRow ? lastRow.activeSubscribers : 0;
    const milestoneRows = result.rows.filter((r) => [3, 6, 9, 12, 18, 24, 30, 36].includes(r.month));
    const { g, cac, churnCutoffPct } = computeGrowthRate(inputs);
    const monthlyGrowthPct = (g - 1) * 100;
    const doublingMonths = g > 1 ? Math.log(2) / Math.log(g) : null;

    return (
        <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '10px' }}>
                <StatTile label="Peak outlay reached" value={gbpWhole(result.peakOutlay)} valueColor={PROFIT_BAD_TEXT} />
                <StatTile
                    label="Breaks even overall by"
                    value={result.breakEvenMonth ? `Month ${result.breakEvenMonth}` : `Not within ${inputs.months} months`}
                    valueColor={result.breakEvenMonth ? PROFIT_GOOD_TEXT : PROFIT_BAD_TEXT}
                />
                <StatTile label={`Savings pot (untouched, ${inputs.profitTakePct}%)`} value={gbpWhole(result.cumWithdrawn)} valueColor={PROFIT_GOOD_TEXT} />
                <StatTile label="Active subscribers at end" value={wholeNum(finalActive)} />
                <StatTile
                    label="Growth rate once self-funding"
                    value={`${monthlyGrowthPct >= 0 ? '+' : ''}${monthlyGrowthPct.toFixed(1)}%/mo${doublingMonths ? ` (~${doublingMonths.toFixed(1)}mo to double)` : ''}`}
                    valueColor={monthlyGrowthPct >= 0 ? PROFIT_GOOD_TEXT : PROFIT_BAD_TEXT}
                />
            </div>
            <p style={{ fontSize: '12px', color: '#898781', marginTop: 0, marginBottom: '20px' }}>
                Once self-funding, profit/spend/subscribers all compound by that growth rate every month, uncapped.
                Biggest lever is subscription amount ÷ CAC (currently {gbpWhole(inputs.subAmount)} ÷ {gbpWhole(cac)}) - cut
                CAC (lower CPC, higher conversion) or raise price and growth accelerates. Next is how much profit
                you reinvest ({100 - inputs.profitTakePct}% currently, via the slider above). Churn drags growth
                down and would flip this scenario to shrinking above ~{churnCutoffPct.toFixed(0)}% monthly churn.
            </p>
            <p style={{ fontSize: '12px', color: '#898781', marginTop: 0, marginBottom: '20px' }}>
                This assumes reinvestment decisions happen monthly, tied to the monthly billing cycle - already as
                fast as recurring (renewal) revenue can be redeployed, since a subscriber genuinely can't pay again
                sooner than their next bill. What it doesn't reinvest at all is each cohort's own first (day-1)
                payment - that's treated here as realized profit, not fuel for more spend. Reinvesting that slice
                too, on a faster cadence than monthly (e.g. fortnightly), would compound noticeably faster in
                theory - but real payout delays (funds aren't spendable the instant they're charged) and Meta's
                ad-learning-phase disruption from frequent budget changes mean faster isn't free, so it isn't
                modeled as the default here.
            </p>

            <ProfitTakeOptimizer inputs={inputs} />

            <StagedProfitPlan inputs={inputs} />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '10px' }}>
                <div>
                    <h4 style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#52514e' }}>Monthly profits taken out</h4>
                    <CohortTrendChart
                        data={chartData}
                        dataKey="withdrawal"
                        name="Monthly profits taken out"
                        color={SERIES_COLORS[1]}
                        yFormatter={(v) => gbpCompact(v)}
                        yLabel="£"
                    />
                </div>
                <div>
                    <h4 style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#52514e' }}>Active subscribers</h4>
                    <CohortTrendChart
                        data={chartData}
                        dataKey="activeSubscribers"
                        name="Active subscribers"
                        color={SERIES_COLORS[0]}
                        yFormatter={(v) => wholeNum(v)}
                        yLabel="Subscribers"
                    />
                </div>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                            <th style={{ padding: '8px' }}>Month</th>
                            <th style={{ padding: '8px' }}>Monthly revenue</th>
                            <th style={{ padding: '8px' }}>Monthly profit/loss</th>
                            <th style={{ padding: '8px' }}>Monthly profit taken out</th>
                        </tr>
                    </thead>
                    <tbody>
                        {milestoneRows.map((r) => {
                            const monthlyProfitLoss = r.revenue - r.spend;
                            return (
                                <tr key={r.month} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '8px' }}>{r.month}</td>
                                    <td style={{ padding: '8px' }}>{gbpWhole(r.revenue)}</td>
                                    <td style={{ padding: '8px', color: monthlyProfitLoss >= 0 ? PROFIT_GOOD_TEXT : PROFIT_BAD_TEXT }}>
                                        {monthlyProfitLoss >= 0 ? '+' : ''}{gbpWhole(monthlyProfitLoss)}
                                    </td>
                                    <td style={{ padding: '8px' }}>{gbpWhole(r.withdrawal)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <p style={{ fontSize: '12px', color: '#898781', marginTop: '10px', marginBottom: 0 }}>
                Models an ongoing campaign that keeps acquiring subscribers every month. Before the campaign has
                paid for itself overall, it spends up to your monthly ad budget each month - throttled down if that
                would push cumulative losses past "max outlay at risk" - and every new cohort's own first payment
                counts toward that same month's income and active-subscriber count right away. Once overall income
                has caught up with spend, the monthly budget no longer applies: every month's profit is split
                exactly {inputs.profitTakePct}% into the untouched savings pot and {100 - inputs.profitTakePct}%
                reinvested into new spend, whatever that comes to - reinvestment simply scales with actual profit
                from there, so growth compounds rather than staying capped at a fixed rate. Formula-only,
                illustrative - a real campaign wouldn't scale this smoothly, and this ignores diminishing returns
                from exhausting your addressable Meta audience as spend grows (a real campaign would eventually hit
                a ceiling this model doesn't model).
            </p>
        </>
    );
}

function BudgetCohortModel() {
    const [inputs, setInputs] = useState({
        subAmount: 5, cpc: 0.50, convertPct: 6, churnPct: 15, months: 36,
        maxOutlay: 1000, monthlyBudget: 500, profitTakePct: 10, targetMonthlyWithdrawal: 8000,
    });

    return (
        <SectionCard title="Budget & cohort financial model" collapsible defaultOpen>
            <p style={{ fontSize: '13px', color: '#666', marginTop: 0, marginBottom: '15px' }}>
                Models an ongoing ad campaign that keeps spending and acquiring subscribers every month: up to a
                realistic monthly budget while still funding growth out of pocket (throttled down further if that
                would push cumulative losses past how much you're willing to have outstanding at once) - then, once
                the campaign has paid for itself overall, switching to reinvesting a fixed share of profit every
                month (uncapped) and withdrawing the rest untouched. Formula-only, illustrative.
            </p>

            <BudgetModelInputs inputs={inputs} setInputs={setInputs} />

            <ReinvestmentView inputs={inputs} />
        </SectionCard>
    );
}

// General industry rules of thumb, not okgather-specific and not a target -
// context for sanity-checking the numbers elsewhere on this page, not
// something to optimize toward for its own sake. Figures vary heavily by
// niche/creative/targeting/region; CPC in particular is USD-denominated in
// most published benchmarks (typically Meta traffic-objective campaigns
// worldwide, not GBP or any specific vertical).
function IndustryBenchmarksNote() {
    const [open, setOpen] = useState(false);
    return (
        <div style={{
            background: '#eef4fc', border: '1px solid #cfe0f5', borderRadius: '8px',
            padding: '14px 16px', marginBottom: '25px', fontSize: '13px', color: '#334155',
        }}>
            <div
                style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none', fontWeight: 600, marginBottom: open ? '6px' : 0 }}
                onClick={() => setOpen((o) => !o)}
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
            >
                <span style={{ fontSize: '11px', display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
                Typical Meta ads / B2C subscription benchmarks (general industry, not a target)
            </div>
            {open && (
                <ul style={{ margin: 0, paddingLeft: '18px' }}>
                    <li>Meta CPC: commonly $0.70–$1.92 depending on campaign objective (traffic vs. lead-gen); Facebook
                        averages around $0.94, Instagram around $1.17 - can swing 30–50%+ either way by niche/creative/targeting.</li>
                    <li>CAC payback: 3–9 months is typical for consumer subscriptions, 2–3 months at the top decile;
                        under 12 months is generally considered healthy.</li>
                    <li>LTV:CAC ratio: 3:1 is the usual minimum bar, 5:1+ is excellent; consumer subscriptions
                        specifically often run closer to 2.5:1–4:1 because churn shortens lifetime value.</li>
                    <li>Monthly churn: 5–10% is typical for consumer subscriptions; under 3–5% is best-in-class.</li>
                </ul>
            )}
        </div>
    );
}

// Where should effort go: cutting CPC, lifting conversion, or charging more?
// All three ultimately act through one ratio - subAmount/CAC - which drives
// both day-1 profit and the compounding growth rate above. Two clean,
// exact results fall out of that:
//   1. CAC = CPC / (conversion/100), so CPC and conversion have an EXACTLY
//      symmetric, opposite 1:1 effect on CAC in relative terms - cutting
//      CPC 10% or lifting conversion 10% reduce CAC by the same 10%. This
//      is exactly what a cheaper-but-less-relevant audience trades against
//      a pricier-but-better-converting one, or new creative that raises
//      CTR (cutting CPC) at the cost of attracting a less qualified click.
//   2. A price rise of P% keeps subAmount/CAC unchanged only if conversion
//      doesn't drop by more than P/(1+P) in relative terms (e.g. +20%
//      price tolerates losing up to ~16.7% of conversion before the ratio
//      - and so day-1 profit and growth rate - gets worse, not better).
function priceBreakEvenConvertDropFrac(priceChangeFrac) {
    if (priceChangeFrac <= -1) return null;
    return priceChangeFrac / (1 + priceChangeFrac);
}

function LeverScenarioTool() {
    const [subAmount, setSubAmount] = useState(5);
    const [cpc, setCpc] = useState(0.5);
    const [convertPct, setConvertPct] = useState(6);
    const [cpcChangePct, setCpcChangePct] = useState(0);
    const [convertChangePct, setConvertChangePct] = useState(0);
    const [priceChangePct, setPriceChangePct] = useState(0);

    const cac = convertPct > 0 ? cpc / (convertPct / 100) : Infinity;
    const ratio = isFinite(cac) && cac > 0 ? subAmount / cac : 0;
    const day1Profit = subAmount - cac;

    const newCpc = cpc * (1 + cpcChangePct / 100);
    const newConvertPct = convertPct * (1 + convertChangePct / 100);
    const newSubAmount = subAmount * (1 + priceChangePct / 100);
    const newCac = newConvertPct > 0 ? newCpc / (newConvertPct / 100) : Infinity;
    const newRatio = isFinite(newCac) && newCac > 0 ? newSubAmount / newCac : 0;
    const newDay1Profit = newSubAmount - newCac;

    const ratioChangePct = ratio > 0 ? ((newRatio - ratio) / ratio) * 100 : 0;
    const isBetter = ratioChangePct > 0.05;
    const isWorse = ratioChangePct < -0.05;

    const priceBreakEven = priceBreakEvenConvertDropFrac(priceChangePct / 100);

    return (
        <>
            <p style={{ fontSize: '13px', color: '#666', marginTop: 0, marginBottom: '15px' }}>
                Cutting CPC, lifting conversion, and raising price all ultimately move the same thing - subscription
                amount ÷ CAC (the ratio behind both day-1 profit and the compounding growth rate above) - so the real
                question isn't which lever is inherently strongest, it's which is cheapest to move without giving
                back more on another lever than you gained. Two exact results: CPC and conversion have a perfectly
                symmetric 1:1 effect on CAC in relative terms (a 10% CPC cut and a 10% conversion lift reduce CAC
                equally) - so a cheaper-but-less-relevant audience or CTR-boosting creative only pays off if the
                conversion it costs you is smaller than the CPC it saves. And a price rise of P% only helps if
                conversion doesn't drop by more than P/(1+P) in relative terms - e.g. +20% price tolerates losing up
                to ~16.7% of conversion before the ratio gets worse rather than better.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '15px' }}>
                <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 160px' }}>
                    Subscription amount ({gbpWhole(subAmount)}/mo)
                    <input type="range" min="3" max="9" step="1" value={subAmount} onChange={(e) => setSubAmount(parseFloat(e.target.value))} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
                </label>
                <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 160px' }}>
                    CPC ({gbp(cpc)})
                    <input type="range" min={REALISTIC_CPC_MIN} max={REALISTIC_CPC_MAX} step="0.01" value={cpc} onChange={(e) => setCpc(parseFloat(e.target.value))} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
                </label>
                <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 160px' }}>
                    Click→subscription conversion ({convertPct}%)
                    <input type="range" min="2" max="10" step="1" value={convertPct} onChange={(e) => setConvertPct(parseFloat(e.target.value))} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
                </label>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '15px' }}>
                <StatTile label="Current CAC" value={gbpWhole(cac)} />
                <StatTile label="Sub. amount ÷ CAC" value={ratio.toFixed(2)} />
                <StatTile label="Day-1 profit/loss" value={`${day1Profit >= 0 ? '+' : ''}${gbpWhole(day1Profit)}`} valueColor={day1Profit >= 0 ? PROFIT_GOOD_TEXT : PROFIT_BAD_TEXT} />
            </div>

            <h5 style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#52514e' }}>Try a scenario</h5>
            <p style={{ fontSize: '12px', color: '#898781', marginTop: 0, marginBottom: '10px' }}>
                Dial in a hypothetical combined change - e.g. a new audience that cuts CPC but also conversion, or a
                price rise with an assumed conversion hit - and see the net effect on the ratio that actually matters.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '15px' }}>
                <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                    CPC change ({cpcChangePct >= 0 ? '+' : ''}{cpcChangePct}%)
                    <input type="range" min="-50" max="30" step="5" value={cpcChangePct} onChange={(e) => setCpcChangePct(parseInt(e.target.value, 10))} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
                </label>
                <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                    Conversion change ({convertChangePct >= 0 ? '+' : ''}{convertChangePct}%)
                    <input type="range" min="-50" max="50" step="5" value={convertChangePct} onChange={(e) => setConvertChangePct(parseInt(e.target.value, 10))} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
                </label>
                <label style={{ fontSize: '12px', color: '#52514e', flex: '1 1 200px' }}>
                    Price change ({priceChangePct >= 0 ? '+' : ''}{priceChangePct}%)
                    <input type="range" min="-30" max="100" step="5" value={priceChangePct} onChange={(e) => setPriceChangePct(parseInt(e.target.value, 10))} onPointerDown={guardSliderThumb} style={{ width: '100%', touchAction: 'pan-y' }} />
                </label>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: '6px', marginBottom: '15px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                            <th style={{ padding: '8px' }}></th>
                            <th style={{ padding: '8px' }}>CPC</th>
                            <th style={{ padding: '8px' }}>Conversion</th>
                            <th style={{ padding: '8px' }}>Sub. amount</th>
                            <th style={{ padding: '8px' }}>CAC</th>
                            <th style={{ padding: '8px' }}>Day-1 profit/loss</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '8px', color: '#898781' }}>Now</td>
                            <td style={{ padding: '8px' }}>{gbp(cpc)}</td>
                            <td style={{ padding: '8px' }}>{convertPct}%</td>
                            <td style={{ padding: '8px' }}>{gbpWhole(subAmount)}</td>
                            <td style={{ padding: '8px' }}>{gbpWhole(cac)}</td>
                            <td style={{ padding: '8px', color: day1Profit >= 0 ? PROFIT_GOOD_TEXT : PROFIT_BAD_TEXT }}>{day1Profit >= 0 ? '+' : ''}{gbpWhole(day1Profit)}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', color: '#898781' }}>Scenario</td>
                            <td style={{ padding: '8px' }}>{gbp(newCpc)}</td>
                            <td style={{ padding: '8px' }}>{newConvertPct.toFixed(1)}%</td>
                            <td style={{ padding: '8px' }}>{gbpWhole(newSubAmount)}</td>
                            <td style={{ padding: '8px' }}>{gbpWhole(newCac)}</td>
                            <td style={{ padding: '8px', color: newDay1Profit >= 0 ? PROFIT_GOOD_TEXT : PROFIT_BAD_TEXT }}>{newDay1Profit >= 0 ? '+' : ''}{gbpWhole(newDay1Profit)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style={{
                padding: '10px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
                backgroundColor: isBetter ? PROFIT_GOOD_BG : isWorse ? PROFIT_BAD_BG : '#f4f3ef',
                color: isBetter ? PROFIT_GOOD_TEXT : isWorse ? PROFIT_BAD_TEXT : '#52514e',
            }}>
                Sub. amount ÷ CAC {ratioChangePct >= 0 ? 'improves' : 'worsens'} by {Math.abs(ratioChangePct).toFixed(1)}%
                {isBetter ? ' - net win, this scenario is worth pursuing' : isWorse ? ' - net loss, the trade-off costs more than it gains' : ' - roughly breakeven'}
                {priceChangePct !== 0 && priceBreakEven !== null && (
                    <span style={{ display: 'block', fontWeight: 400, marginTop: '4px' }}>
                        At a {priceChangePct >= 0 ? '+' : ''}{priceChangePct}% price change, conversion breaks even at a
                        {' '}{(priceBreakEven * 100).toFixed(1)}% relative {priceBreakEven >= 0 ? 'drop' : 'rise'} (you've set {convertChangePct >= 0 ? '+' : ''}{convertChangePct}%).
                    </span>
                )}
            </div>
        </>
    );
}

function AdminAds({ tabName }) {
    const [variations, setVariations] = useState(DEFAULT_VARIATIONS);
    const [churnPct, setChurnPct] = useState(10);
    const isMobile = useIsMobile();

    const updateVariation = (index, next) => {
        setVariations((prev) => prev.map((v, i) => (i === index ? next : v)));
    };

    const rows = variations.map((v) => ({ v, metrics: computeMetrics(v, churnPct) }));

    return (
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '10px' : '20px' }}>
            <h3>{tabName}</h3>
            <p style={{ color: '#666', fontSize: '14px', marginTop: '-10px', marginBottom: '25px' }}>
                Compare up to 4 CPC / conversion-rate / subscription-price combinations to see which are a{' '}
                <strong>self-liquidating offer</strong> (CAC ≤ subscription amount, i.e. the first payment covers
                what it cost to acquire the subscriber) versus which lose money on the initial purchase and need
                later renewals to break even.
            </p>

            <LtvCacSummary rows={rows} />

            <IndustryBenchmarksNote />

            <BudgetCohortModel />

            <SectionCard title="Ads profitability calculator" collapsible defaultOpen={false}>
                <p style={{ fontSize: '13px', color: '#666', marginTop: 0, marginBottom: '15px' }}>
                    CAC = CPC ÷ conversion rate. Profit/Loss = subscription amount − CAC. This is a client-side
                    "what-if" tool only - nothing here is saved or pulled from real ad spend/Stripe data.
                </p>

                <label style={{ fontSize: '12px', color: '#52514e', display: 'block', marginBottom: '15px', maxWidth: '420px' }}>
                    Assumed monthly churn ({churnPct}% cancel each month)
                    <input
                        type="range" min="0" max="40" step="1" value={churnPct}
                        onChange={(e) => setChurnPct(parseInt(e.target.value, 10))}
                        onPointerDown={guardSliderThumb}
                        style={{ width: '100%', touchAction: 'pan-y' }}
                    />
                    <span style={{ display: 'block', color: '#898781' }}>
                        Drives each variation's payback period below - CAC alone doesn't say how long it actually
                        takes to earn back if some subscribers cancel along the way.
                    </span>
                </label>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                    {variations.map((variation, i) => (
                        <VariationColumn
                            key={i}
                            variation={variation}
                            onChange={(next) => updateVariation(i, next)}
                            churnPct={churnPct}
                        />
                    ))}
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '11px' : '13px', tableLayout: isMobile ? 'fixed' : 'auto' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                                <th style={{ padding: isMobile ? '4px' : '8px', width: isMobile ? '20%' : 'auto' }}>Variation</th>
                                <th style={{ padding: isMobile ? '4px' : '8px', width: isMobile ? '10%' : 'auto' }}>CPC</th>
                                <th style={{ padding: isMobile ? '4px' : '8px', width: isMobile ? '9%' : 'auto' }}>{isMobile ? 'Conv.' : 'Conversion'}</th>
                                <th style={{ padding: isMobile ? '4px' : '8px', width: isMobile ? '9%' : 'auto' }}>{isMobile ? 'Sub.' : 'Sub. amount'}</th>
                                <th style={{ padding: isMobile ? '4px' : '8px', width: isMobile ? '21%' : 'auto' }}>CAC</th>
                                <th style={{ padding: isMobile ? '4px' : '8px', width: isMobile ? '23%' : 'auto' }}>{isMobile ? 'P/L' : 'Profit/Loss'}</th>
                                <th style={{ padding: isMobile ? '4px' : '8px', width: isMobile ? '8%' : 'auto' }}>{isMobile ? '✓?' : 'Viable?'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(({ v, metrics }, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: isMobile ? '4px' : '8px', whiteSpace: isMobile ? 'nowrap' : 'normal', overflow: isMobile ? 'hidden' : 'visible', textOverflow: isMobile ? 'ellipsis' : 'clip' }} title={v.label}>{v.label}</td>
                                    <td style={{ padding: isMobile ? '4px' : '8px' }}>{v.cpc ? `£${v.cpc}` : '—'}</td>
                                    <td style={{ padding: isMobile ? '4px' : '8px' }}>{v.convertPct ? `${v.convertPct}%` : '—'}</td>
                                    <td style={{ padding: isMobile ? '4px' : '8px' }}>{v.subAmount ? `£${v.subAmount}` : '—'}</td>
                                    <td style={{ padding: isMobile ? '4px' : '8px' }}>{metrics ? gbpWhole(metrics.cac) : '—'}</td>
                                    <td style={{ padding: isMobile ? '4px' : '8px', color: metrics ? (metrics.profitLoss >= 0 ? '#155724' : '#721c24') : 'inherit', whiteSpace: isMobile ? 'nowrap' : 'normal' }}>
                                        {metrics ? `${metrics.profitLoss >= 0 ? '+' : ''}${gbpWhole(metrics.profitLoss)}` : '—'}
                                    </td>
                                    <td style={{ padding: isMobile ? '4px' : '8px' }}>
                                        {metrics ? (metrics.isSelfLiquidating ? '✅' : '❌') : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </SectionCard>

            <CacBreakevenExplorer />

            <SectionCard title="Where to focus: CPC, conversion, or price?" collapsible defaultOpen={false}>
                <LeverScenarioTool />
            </SectionCard>
        </div>
    );
}

export default AdminAds;
